import type { ReactElement } from 'react'
import { isSelectableLeadStage } from '@/lib/leads'
import { Link } from '@tanstack/react-router'
import {
  CalendarDays,
  Check,
  ExternalLink,
  FolderOpen,
  GitBranch,
  LoaderCircle,
} from 'lucide-react'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuGroup,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from '@/components/ui/context-menu'
import { InquiryStageEnum } from '@/gen/cortado/v1/settings_pb'
import { formatEnumName, getInquiryStageColor, isBooked } from '@/lib/enum-utils'
import { cn } from '@/lib/utils'
import { useInquiry } from '@/stores/leads.store'
import { toast } from 'sonner'
import { badgeVariantToTailwind } from './stage-colors'
import type { Lead } from './types'

interface LeadContextMenuProps {
  lead: Lead
  stages: InquiryStageEnum[]
  onStageChange: (
    lead: Lead,
    newStage: InquiryStageEnum,
  ) => void | Promise<void>
  children: ReactElement
}

function LeadEventMenuItem({ lead }: { lead: Lead }) {
  const { inquiry, isLoading } = useInquiry(lead.id)
  const eventId = inquiry?.eventIds[0]

  if (isLoading) {
    return (
      <ContextMenuItem disabled>
        <LoaderCircle className="animate-spin" />
        Loading event…
      </ContextMenuItem>
    )
  }

  if (!eventId) return null

  return (
    <ContextMenuItem asChild>
      <Link to="/events/$id" params={{ id: eventId }} preload="intent">
        <CalendarDays />
        Open event
      </Link>
    </ContextMenuItem>
  )
}

export function LeadContextMenu({
  lead,
  stages,
  onStageChange,
  children,
}: LeadContextMenuProps) {
  const handleStageChange = (newStage: InquiryStageEnum) => {
    if (newStage === lead.stage) return

    void Promise.resolve(onStageChange(lead, newStage)).catch(() => {
      toast.error('Failed to update stage. Please try again.')
    })
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent
        className="w-56"
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
      >
        <ContextMenuGroup>
          <ContextMenuItem asChild>
            <Link to="/leads/$id" params={{ id: lead.id }} preload="intent">
              <FolderOpen />
              Open lead
            </Link>
          </ContextMenuItem>
          <ContextMenuItem asChild>
            <Link
              to="/leads/$id"
              params={{ id: lead.id }}
              preload="intent"
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink />
              Open lead in new tab
            </Link>
          </ContextMenuItem>
          {isBooked(lead) && <LeadEventMenuItem lead={lead} />}
        </ContextMenuGroup>
        <ContextMenuSeparator />
        <ContextMenuGroup>
          <ContextMenuSub>
            <ContextMenuSubTrigger className="gap-2">
              <GitBranch />
              Change stage
            </ContextMenuSubTrigger>
            <ContextMenuSubContent className="w-52">
              <ContextMenuGroup>
                {stages.filter(isSelectableLeadStage).map((stage) => {
                  const isCurrent = stage === lead.stage
                  return (
                    <ContextMenuItem
                      key={stage}
                      disabled={isCurrent}
                      onSelect={() => handleStageChange(stage)}
                    >
                      <span
                        data-slot="stage-indicator"
                        aria-hidden="true"
                        className={cn(
                          'size-2 shrink-0 rounded-full',
                          badgeVariantToTailwind(getInquiryStageColor(stage)),
                        )}
                      />
                      {formatEnumName(stage, InquiryStageEnum)}
                      {isCurrent && <Check className="ml-auto" />}
                    </ContextMenuItem>
                  )
                })}
              </ContextMenuGroup>
            </ContextMenuSubContent>
          </ContextMenuSub>
        </ContextMenuGroup>
      </ContextMenuContent>
    </ContextMenu>
  )
}
